import {Component, OnInit, Inject, OnDestroy, Output, EventEmitter, ChangeDetectorRef, inject} from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
  InteractionType,
  AccountInfo,
  SsoSilentRequest,
  IdTokenClaims,
  PromptValue,
  IPublicClientApplication
} from '@azure/msal-browser';
import {combineLatestWith, forkJoin, Subject} from 'rxjs';
import {catchError, combineLatest, filter, takeUntil, tap} from 'rxjs/operators';

import { environment } from '../environments/environment';
import { Router, NavigationEnd, NavigationError } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import {ModulesService} from './services/modules.service';
import {StickyNotesService} from './sticky-notes/services/sticky-notes.service';
import {IModuleMenu} from './models/module/IModuleMenu';
import {CompaniesService} from './services/companies.service';
import {RedirectHandlerService} from './auth/services/test/redirect-handler.service';
import {CustomersLoginMsalService} from './auth/msal/services/customers-login-msal.service';
import {UserExtendedDTO} from './models/user/user-b2c-flow-dto.model';
import {b2cPolicyOf, staffUserFromClaims} from './auth/msal/staff-claims';
import {of} from 'rxjs/internal/observable/of';
import {UserService} from './services/user.service';
import {ORG_SCOPED_KEYS, OrgScopeStore} from './services/org-scope-store';
import {ReadOnlyUiGuard} from './permissions/read-only-ui-guard.service';
import {clearNotesGrant} from './permissions/notes-access';
import {FormDraftsService} from './shared/form-drafts/form-drafts.service';
import {PresenceService} from './core/presence/presence.service';
import {LinkContextMenuService} from './shared/context-menu/link-context-menu.service';
import {BRAND} from './config/brand';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string,
    tfp?: string,
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        ModulesService,
        CustomersLoginMsalService
    ],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    title = BRAND.name;
    isIframe = false;
    loginDisplay = false;
    public showHint = false;
    public menu = [];
    private readonly _destroying$ = new Subject<void>();
    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private redirectHandler: RedirectHandlerService,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router,
        private permissionsService: NgxPermissionsService,
        private modulesService: ModulesService,
        private companiesService: CompaniesService,
        private cdR: ChangeDetectorRef,
        private userService: UserService,
        private stickyNotes: StickyNotesService,
        private readOnlyUiGuard: ReadOnlyUiGuard,
        private formDrafts: FormDraftsService,
        private presence: PresenceService,
        private linkContextMenu: LinkContextMenuService,
    ) { }




    ngOnInit(): void {
        // Sticky-note reminders must fire wherever the user is, not only on the dashboard that
        // owns the notes, so the watcher is started once here at boot.
        this.stickyNotes.startReminderWatch();

        // Stops a read-only Viewer's clicks on write controls and dims them. Deliberately NOT a
        // whole-page MutationObserver — see ReadOnlyUiGuard's class comment for why that froze the
        // app previously and what replaced it. The HTTP interceptor is the real backstop; this is UX.
        this.readOnlyUiGuard.start();

        // Right-click → Open / Open in new tab / Copy link on anything that navigates. Started
        // here, before the shell's children run their first ngOnChanges, so every routerLink is tagged.
        this.linkContextMenu.start();

        // A form left unsaved when a full-page redirect (typically the sign-in after an expired
        // session) tore the page down was stashed in this tab; hand it to the server once signed
        // in again. Forms reopened before then pick their stash up themselves.
        setTimeout(() => {
            if (this.authService.instance.getAllAccounts().length > 0) this.formDrafts.uploadStashed();
        }, 5000);

        this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

        // Handle BOC OAuth callback — when BOC redirects back to the frontend URL with ?code=
        const params = new URLSearchParams(window.location.search);
        const bocCode = params.get('code');
        const bocError = params.get('error');
        if (window.location.pathname.includes('/api/openbanking/boc/auth/callback')) {
            if (bocError) {
                if (window.opener) {
                    window.opener.postMessage({ type: 'boc_auth_error', error: `BOC authorization failed: ${bocError}` }, '*');
                }
            } else if (bocCode) {
                if (window.opener) {
                    window.opener.postMessage({ type: 'boc_code', code: bocCode }, '*');
                }
            }
            setTimeout(() => window.close(), 500);
            return;
        }

      this.redirectHandler.waitForRedirect().then(result => {
        // console.log(this.customerMsalService.getActiveAccount());
        if (result?.account) {
          // console.log('[AppComponent] Redirect complete. Account:', result.account);
          //
          // // Optionally: navigate based on callback state
          // const state = new URLSearchParams(window.location.search).get('callback_state');
          // if (state === 'business') {
          //   this.router.navigate(['/business-signup-complete']);
          // } else if (state === 'individual') {
          //   this.router.navigate(['/individual-signup-complete']);
          // } else {
          //   this.router.navigate(['/dashboard']);
          // }
        } else {
          console.log('[AppComponent] No result from redirect');
        }

        // Start normal MSAL broadcast subscriptions now
      });

      this.initMsalListeners();

      // A page's code chunk that fails to load is almost always a deploy: file names are content-
      // hashed, so a tab opened before the release asks for chunks that no longer exist. The router
      // then cancels the navigation silently and the user stays where they were — clicking Notes
      // on Home "just refreshed Home". Load the target URL for real instead, which fetches the new
      // build. Once per URL per tab, so a chunk that is genuinely missing cannot loop the page.
      this.router.events
        .pipe(
          filter((e): e is NavigationError => e instanceof NavigationError),
          takeUntil(this._destroying$),
        )
        .subscribe((e: NavigationError) => {
          const message = String(e.error?.message ?? e.error ?? '');
          if (!/Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed/i.test(message)) return;
          const key = `bc.chunkReload:${e.url}`;
          try {
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
          } catch { /* no storage: still reload once, the browser session ends the loop */ }
          window.location.assign(e.url);
        });
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.authService.instance.getActiveAccount();
        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
          let accounts = this.authService.instance.getAllAccounts();
          this.authService.instance.setActiveAccount(accounts[0]);
        } else if (this.authService.instance.getAllAccounts().length === 0) {
          // Clear only app-managed cache keys; never blow away the whole
          // localStorage — MSAL stores its session there too, and a stray
          // `clear()` mid-redirect-handling wipes the freshly written tokens,
          // sending the user to /login-failed despite a successful B2C sign-in.
          // 'module' is deliberately kept: this branch also fires transiently while MSAL is still
          // resolving accounts, and wiping it there would cost a returning user their last module.
          [...ORG_SCOPED_KEYS.filter(k => k !== 'module'), 'organization']
            .forEach(k => localStorage.removeItem(k));
        }
    }

    loginRedirect() {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    login(userFlowRequest?: RedirectRequest | PopupRequest) {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            } else {
                this.authService.loginPopup(userFlowRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
            } else {
                this.authService.loginRedirect(userFlowRequest);
            }
        }
    }

    logout() {
        // Check out before the token goes away.
        this.presence.stop();

        // Clear only the app's own cached state — NOT all of localStorage. The employee MSAL keeps
        // its tokens/account in localStorage, so a blanket localStorage.clear() here wipes MSAL's
        // cache while logoutRedirect is still building the sign-out request, corrupting the first
        // attempt (that's why logout only worked on the second try). MSAL clears its own cache.
        this.clearAppState();

        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: "/"
            });
        } else {
            this.authService.logoutRedirect({
              postLogoutRedirectUri: '/'
            });
        }
    }

    private clearAppState() {
        // Unsaved form contents must not survive into the next user's session on this browser.
        this.formDrafts.forgetForLogout();

        // ORG_SCOPED_KEYS covers every per-module flag cache plus the company id and active
        // module. Those now have browser-wide defaults (OrgScopeStore.setScoped) so a brand-new
        // tab inherits them — which means logout has to clear the defaults too, or the next user
        // to sign in on this browser starts on the previous one's flags.
        [...ORG_SCOPED_KEYS, 'organization'].forEach(k => localStorage.removeItem(k));
        // Per-company role caches and the tenant bootstrap.
        Object.keys(localStorage)
          .filter(k => k.startsWith('effectivePermissions'))
          .forEach(k => localStorage.removeItem(k));
        sessionStorage.removeItem('bc.tenant.bootstrap');
        clearNotesGrant();
        sessionStorage.removeItem('organization');
        // Org-scoped state now lives per-tab in sessionStorage — clear it too on logout.
        OrgScopeStore.clearTab();
    }

    setPermissionsFromLocalStorage() {
      const parseIfExists = (key: string): string[] => {
        const val = OrgScopeStore.getItem(key);
        try {
          return val ? JSON.parse(val) : [];
        } catch (e) {
          console.warn(`Invalid permissions in localStorage for key "${key}":`, e);
          return [];
        }
      };

      // Onboarding + every module's cached permission strings. Module keys come from ModulesService
      // so a new module's permissions are picked up automatically (no hardcoded list to maintain).
      const keys = ['permissions', ...this.modulesService.getAllPermissionStorageKeys()];
      const combinedPermissions = keys.flatMap(k => parseIfExists(k));

      // Backward-compat: the Compliance module was historically cached under the legacy "Risk"
      // permission names. Map them to the current names so sessions that granted access before the
      // rename aren't locked out until they re-login. Safe to remove once caches have cycled.
      if (combinedPermissions.includes('RiskUser')) combinedPermissions.push('ComplianceUser');
      if (combinedPermissions.includes('RiskAdmin')) combinedPermissions.push('ComplianceAdmin');

      this.permissionsService.loadPermissions(combinedPermissions);
    }

    setCompanyIdObservable() {
      if (this.authService.instance.getAllAccounts().length > 0) {  // Only set if logged in
        const companyId = +(OrgScopeStore.getItem('managedCompanyId') ?? 0) as number;
        if (companyId) {
          this.companiesService.setCompanyIdObs(companyId);
        }
      }
    }

    redirectToOrganization()
    {
        if (localStorage.getItem('organization')) {
            this.router.navigate(['settings/organization']);
        }
    }

    editProfile() {
        // let editProfileFlowRequest: RedirectRequest | PopupRequest  = {
        //     authority: environment.b2cPolicies.authorities.editProfile.authority,
        //     scopes: [],
        // };

        // this.login(editProfileFlowRequest);
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

  changeModule(id: number) {
   this.modulesService.selectModule(id)
  }

  changeMenu(menu: IModuleMenu[]) {
    this.menu = menu;
    this.cdR.detectChanges();
  }

  private initMsalListeners(): void {
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });


    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        // Automatic check-in: the user counts as in the system while any tab is open. Idempotent,
        // and only once sign-in has settled so the first heartbeat has an account to use.
        if (this.loginDisplay && !this.isIframe) this.presence.start();
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {

        let payload = result.payload as AuthenticationResult;
        let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        this.checkNewUser(idtoken);

        // A workforce token (the Klidi deployment signs staff in to bizcombo.tech) has no B2C policy: every sign-in
        // there is the sign-in flow.
        const policy = b2cPolicyOf(idtoken);
        if (!policy || policy === environment.b2cPolicies.names.signUpSignIn) {
          this.authService.instance.setActiveAccount(payload.account);
        }

        /**
         * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
         * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
         * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        if (idtoken.acr === environment.b2cPolicies.names.editProfile || idtoken.tfp === environment.b2cPolicies.names.editProfile) {

          // retrieve the account from initial sing-in to the app
          const originalSignInAccount = this.authService.instance.getAllAccounts()
            .find((account: AccountInfo) =>
              account.idTokenClaims?.oid === idtoken.oid
              && account.idTokenClaims?.sub === idtoken.sub
              && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === environment.b2cPolicies.names.signUpSignIn
                || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === environment.b2cPolicies.names.signUpSignIn)
            );

          let signUpSignInFlowRequest: SsoSilentRequest = {
            authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
            account: originalSignInAccount
          };

          // silently login again with the signUpSignIn policy
          this.authService.ssoSilent(signUpSignInFlowRequest);
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         * If you do not want this behavior and prefer your users to stay signed in instead,
         * you can replace the code below with the same pattern used for handling the return from
         * profile edit flow (see above ln. 74-92).
         */
        if (idtoken.acr === environment.b2cPolicies.names.resetPassword || idtoken.tfp === environment.b2cPolicies.names.resetPassword) {
          let signUpSignInFlowRequest: RedirectRequest | PopupRequest  = {
            authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
            scopes: [...environment.apiConfig.scopes],
            prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
          };

          this.login(signUpSignInFlowRequest);
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          let resetPasswordFlowRequest: RedirectRequest | PopupRequest  = {
            authority: environment.b2cPolicies.authorities.resetPassword.authority,
            scopes: [],
          };

          this.login(resetPasswordFlowRequest);
        };
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Only force a logout for genuine session-lost errors. Transient/interactive
        // failures (popup blocked, redirect in progress, user cancelled, network
        // hiccup) — common on mobile/iOS Safari — would otherwise wipe a freshly
        // completed login and bounce the user to /login-failed.
        const errorCode = (result.error as any)?.errorCode ?? '';
        const errorMessage = result.error?.message ?? '';
        const transientCodes = [
          'interaction_in_progress',
          'popup_window_error',
          'empty_window_error',
          'user_cancelled',
          'no_network_connectivity',
          'monitor_window_timeout',
          'block_iframe_reload',
        ];
        const isTransient = transientCodes.some(
          c => errorCode === c || errorMessage.indexOf(c) > -1
        );
        if (isTransient) {
          return;
        }
        // Genuine session-lost errors: silent acquisition failed and the user
        // must sign in again. A cached account object survives refresh-token
        // expiry, so these would otherwise fall through to the active-account
        // guard below and get swallowed — leaving the app stuck (no logout, no
        // re-login). Force an interactive redirect to re-authenticate.
        const interactionRequiredCodes = [
          'interaction_required',
          'login_required',
          'consent_required',
          'refresh_token_expired',
          'AADB2C90077',
        ];
        const needsInteraction = interactionRequiredCodes.some(
          c => errorCode === c || errorMessage.indexOf(c) > -1
        );
        if (needsInteraction) {
          this.login();
          return;
        }
        // Don't logout while MSAL is still mid-interaction — let the flow settle.
        if (this.authService.instance.getActiveAccount() && this.loginDisplay) {
          // Session exists; surface error but keep the user logged in.
          console.warn('[MSAL] Acquire token failure (non-fatal):', errorCode, errorMessage);
          return;
        }
        this.logout();
      });

    this.setPermissionsFromLocalStorage();
    this.setCompanyIdObservable();
  }

 private checkNewUser(claims: any) {
      const user: UserExtendedDTO = staffUserFromClaims(claims);

      return this.userService.createUserfromADb2c(user).pipe(
        catchError(error => {
          throw error; // rethrow the error
        })
      ).subscribe();
    }
}
